import React from 'react';
import { useFormikContext } from 'formik';
import { uniqueId, isEqual } from 'lodash';
import { LayoutGrid } from 'lucide-react';

import Button from '@dashboard/components/Button';
import { FormElement } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import { DEFAULT_NEW_PRICE } from './PricesList/PricesList';
import { persistedFields } from './PricesList/priceHelpers';
import PriceForm from './PricesList/PriceForm';
import Divider from '@/dashboard/components/Divider';

// TODO(pricing-type-revival): temporary revival of pricingType 'multiple'
// ("priced with variants": no base price, the customer's total is the sum of
// the variant options they pick). The storefront and API already handle a lone
// amount-0 price; this module only supplies the dashboard mode UI. Grep for
// TODO(pricing-type-revival) to remove the whole feature.

/**
 * The one gate for the "priced with variants" dashboard mode. Guards beyond
 * the flag itself protect legacy drift: every dashboard save round-trips the
 * stored pricingType, so post-migration products can carry 'multiple' while
 * holding a real amount (or a PWYW reinterpretation) — those must keep the
 * normal price form.
 */
export function isVariantsPriced(values) {
  if (values.pricingType !== 'multiple') return false;
  const prices = values.prices || [];
  if (prices.length > 1) return false;
  const price = prices[0];
  if (!price) return true;
  if (price.payWhatYouWant) return false;
  return !Number(price.amount || 0);
}

/**
 * A zeroed replacement for the lone price when switching to variants pricing.
 * The price form stays fully editable in this mode (only the base amount is
 * hidden), so the switch preserves everything the merchant configured — it
 * zeroes the amount (→ 0) and clears the PWYW pair (PWYW has no base price to
 * discount from). The Discounted-from strike-through survives: it renders the
 * $0 price line on the storefront. Stale nested recurring/paymentPlan configs
 * are dropped; the flat fields are the source of truth and draftToNestedPrice
 * rebuilds the nested shape on submit.
 */
export function toVariantsPricedPrice(price = {}, currency = 'usd') {
  // eslint-disable-next-line no-unused-vars -- recurring/paymentPlan intentionally dropped
  const { recurring, paymentPlan, ...flat } = price;
  return {
    ...DEFAULT_NEW_PRICE,
    ...flat,
    key: price.key || uniqueId('price-'),
    currency,
    amount: '0',
    payWhatYouWant: false,
    pwywSuggestedPrice: '',
    __pricing: 'fixed',
    // A linked Stripe price carries its own amount — it would reintroduce the
    // base price this mode hides ("Use Stripe price" is hidden too).
    managedInStripe: false,
    stripePriceId: '',
    enabled: true,
  };
}

function SwitchLink({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className="underline font-medium text-foreground">
      {children}
    </button>
  );
}

export function VariantsPricingLink({ onSwitch }) {
  return (
    <p className="mt-4 text-xs text-center text-muted-foreground">
      Don't need a base price? <SwitchLink onClick={onSwitch}>Use variants instead.</SwitchLink>
    </p>
  );
}

/**
 * The reverse of VariantsPricingLink: switches a variants-priced checkout back
 * to a base price. Instant in the create flow; in the edit flow it starts the
 * Cancel/Save transition (see ProductDetailsForm's pendingBaseSwitch).
 */
export function BasePricingLink({ onSwitch }) {
  return (
    <p className="mt-4 text-xs text-center text-muted-foreground">
      <SwitchLink onClick={onSwitch}>Use a base price instead</SwitchLink>
    </p>
  );
}

/**
 * The one "priced with variants" alert, identical in the create flow and the
 * page edit — it just names the pricing mode. Switching back to a base price is
 * a separate BasePricingLink, not a button in here.
 */
export function VariantsPricedAlert() {
  return (
    <Alert>
      <LayoutGrid className="h-4 w-4" />
      <AlertTitle>No base price</AlertTitle>
      <AlertDescription>
        <p>Customers choose from your variants.</p>
      </AlertDescription>
    </Alert>
  );
}

export function PricedWithVariantsSection({ isNew, titleField, onCancelPricingSwitch }) {
  const { values, initialValues, submitForm } = useFormikContext();
  const price = (values.prices || [])[0];

  /* The full price form minus the base amount — billing, schedule,
     currency/repeats, trials, setup fees etc. stay editable. Create mode
     shows the billing radios (draft form); edit mode mirrors the
     single-price flat form (locked billing). */
  const priceForm = (
    <PriceForm
      fieldPrefix="prices[0]"
      price={price}
      hideAmount
      currencyLocked={(values.prices || []).some(p => p.hasBeenPurchased)}
      lockBilling={!isNew}
      singlePrice={!isNew}
      isNew={isNew}
    />
  );

  // Create flow: the alert leads (below the product title, above the billing
  // choice), the form follows unboxed — matching the normal draft form.
  if (isNew) {
    return (
      <>
        <FormElement>
          <Divider className="mb-4" />
          <VariantsPricedAlert />
        </FormElement>
        <FormElement>{priceForm}</FormElement>
      </>
    );
  }

  // Edit mode persists via this Save (title autosave is off in the
  // lone-committed-price state). The price form stays editable in variants
  // mode (minus the amount), so its fields count toward dirtiness too.
  const isDirty =
    (values.pricingType || 'single') !== (initialValues.pricingType || 'single') ||
    (values.title || '') !== (initialValues.title || '') ||
    !isEqual(
      persistedFields((values.prices || [])[0]),
      persistedFields((initialValues.prices || [])[0]),
    );

  // Edit: the alert leads (above the boxed form), then the product title and
  // the price form share one box, saved together by the box's Save button.
  return (
    <>
      <FormElement>
        <VariantsPricedAlert />
      </FormElement>
      <FormElement>
        <div className="rounded-lg border border-stone-200 dark:border-border p-3">
          {titleField}
          <Divider />
          {priceForm}
          <div className="flex items-center justify-end gap-2">
            <FormSubmit.State visible={isDirty} />
            {/* Set while an unsaved base-price→variants switch is pending:
                Cancel resets the form back to the single-price mode. */}
            {onCancelPricingSwitch && (
              <Button type="button" appearance="light" onClick={onCancelPricingSwitch}>
                Cancel
              </Button>
            )}
            <Button type="button" appearance="primary" onClick={() => submitForm()}>
              Save
            </Button>
          </div>
        </div>
      </FormElement>
    </>
  );
}
